/**
 * UserMemberships.js
 * Component for displaying user memberships with pagination
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Alert,
  Spinner,
  Pagination,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import { membershipService } from '../../services/api';
import { formatPaymentMethod } from '../../helpers/subscriptionHelpers';
import CreateMembershipModal from './CreateMembershipModal';

const UserMemberships = ({ userId }) => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMembership, setEditingMembership] = useState(null);

  const loadMemberships = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await membershipService.getUserMemberships(userId, page, 10);
      
      if (response.success && response.data) {
        setMemberships(response.data.items || []);
        setCurrentPage(response.data.currentPage || 1);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setError('Errore nel caricamento dei tesseramenti');
      }
    } catch (err) {
      console.error('Error loading memberships:', err);
      setError('Errore nel caricamento dei tesseramenti');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadMemberships(1);
    }
  }, [userId, loadMemberships]);

  const handlePageChange = (page) => {
    loadMemberships(page);
  };

  const handleMembershipCreated = (newMembership) => {
    // Ricarica la prima pagina per mostrare il nuovo tesseramento
    loadMemberships(1);
  };

  const handleEditMembership = (membership) => {
    setEditingMembership(membership);
    setShowCreateModal(true);
  };

  const handleMembershipUpdated = (updatedMembership) => {
    // Ricarica la pagina corrente per mostrare le modifiche
    loadMemberships(currentPage);
    setEditingMembership(null);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingMembership(null);
  };

  const handleDeleteMembership = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo tesseramento? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await membershipService.deleteMembership(id);

      if (response.success) {
        // Ricarica la pagina corrente per aggiornare la lista
        loadMemberships(currentPage);
      } else {
        setError(response.message || 'Errore nell\'eliminazione del tesseramento');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error deleting membership:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const datePart = dateString.split('T')[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        if (year && month && day && parseInt(year, 10) >= 1900) {
          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }
      }
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '-';
    return date.toLocaleDateString('it-IT');
  };

  if (loading && currentPage === 1) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento tesseramenti...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="user-memberships">
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-3">
        <Col>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            disabled={loading}
          >
            <i className="fas fa-plus me-2"></i>
            Nuovo tesseramento
          </Button>
        </Col>
      </Row>

      {memberships.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-4">
            <i className="fas fa-id-card fa-3x text-muted mb-3"></i>
            <p className="text-muted mb-0">Nessun tesseramento presente</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Numero Tesseramento</th>
                    <th>Anno Sportivo</th>
                    <th>Importo</th>
                    <th>Data Pagamento</th>
                    <th>Data Scadenza</th>
                    <th>Data Creazione</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((membership) => (
                    <tr key={membership.id || membership.membershipNumber}>
                      <td>
                        <strong>{membership.membershipNumber || '-'}</strong>
                      </td>
                      <td>{membership.academicYear ? `${membership.academicYear}/${membership.academicYear + 1}` : '-'}</td>
                      <td>{membership.amount ? `€ ${membership.amount.toFixed(2)}` : '-'}</td>
                      <td>
                        <div>{formatDate(membership.paidDate)}</div>
                        {membership.paidDate && membership.paymentMethod && (
                          <Badge bg="light" text="dark" className="border mt-1" style={{ fontSize: '0.72rem' }}>
                            {formatPaymentMethod(membership.paymentMethod)}
                          </Badge>
                        )}
                      </td>
                      <td>{formatDate(membership.expiryDate)}</td>
                      <td>{formatDate(membership.createdAt)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          title="Modifica tesseramento"
                          onClick={() => handleEditMembership(membership)}
                          disabled={loading}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Elimina tesseramento"
                          onClick={() => handleDeleteMembership(membership.id)}
                          disabled={loading}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Pagina {currentPage} di {totalPages} ({totalCount} elementi totali)
              </small>
              
              <Pagination className="mb-0">
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }
                  
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === currentPage}
                      onClick={() => handlePageChange(pageNumber)}
                      disabled={loading}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Modal per creare/modificare tesseramento */}
      <CreateMembershipModal
        show={showCreateModal}
        onHide={handleModalClose}
        userId={userId}
        editingMembership={editingMembership}
        latestMembershipNumber={memberships[0]?.membershipNumber || ''}
        onMembershipCreated={handleMembershipCreated}
        onMembershipUpdated={handleMembershipUpdated}
      />
    </div>
  );
};

export default UserMemberships;
